'use client';

import { domAnimation, LazyMotion, m, useReducedMotion } from 'motion/react';
import type { CSSProperties, ReactNode } from 'react';

import { MOTION_POSITION_SMALL } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';

// ---------------------------------------------------------------------------
// AnimatedProgressCircle — circular SVG progress ring. A track circle with a
// filled arc whose `strokeDashoffset` animates from full-circumference (0%) to
// 0 (100%) as `progress` changes.
//
// Ported from the mobile primitive
// (mobile/src/components/ui/animated-progress-circle.tsx). Mobile drives the
// offset with Reanimated `withTiming`; here it runs on Motion (`m.circle`) with
// `MOTION_POSITION_SMALL` (150ms, ease-position) — the same curve + duration the
// mobile primitive uses (`REANIMATED_EASE_POSITION` + `DURATION_POSITION_SMALL_MS`),
// so the two platforms feel identical. The `m.circle` + `strokeDashoffset`
// pattern mirrors `images/generating-frame.tsx`.
//
// Colors come from semantic tokens via `stroke-*` classes (not hex), so callers
// override the palette with `strokeClassName` / `trackClassName` rather than raw
// color strings. The unfilled track defaults to the same `stroke-feedback-success-accent` token
// as the fill at `trackOpacity`, reading as a faded shade of the same green
// rather than a neutral grey ring.
//
// Reduced motion: snaps `strokeDashoffset` to its target (no transition),
// matching the mobile reduce-motion fallback.
// ---------------------------------------------------------------------------

export interface AnimatedProgressCircleProps {
  label?: string;
  /** Progress 0–100. Clamped to that range. */
  progress: number;
  /** Outer diameter (px). @default 40 */
  diameter?: number;
  /** Stroke thickness (px). @default 4 */
  strokeWidth?: number;
  /** Track (unfilled) opacity. @default 0.2 */
  trackOpacity?: number;
  /** Class merged onto the filled arc (default: `stroke-feedback-success-accent`). */
  strokeClassName?: string;
  /** Class merged onto the track circle (default: `stroke-feedback-success-accent`). */
  trackClassName?: string;
  className?: string;
  /** Centered overlay above the ring (e.g. a percentage readout or icon). */
  children?: ReactNode;
}

export function AnimatedProgressCircle({
  label,
  progress,
  diameter = 40,
  strokeWidth = 4,
  trackOpacity = 0.2,
  strokeClassName,
  trackClassName,
  className,
  children,
}: AnimatedProgressCircleProps): React.ReactNode {
  const reduce = useReducedMotion() === true;

  const radius = diameter / 2;
  const circleRadius = radius - strokeWidth / 2;
  const circumference = 2 * Math.PI * circleRadius;

  const clamped = Math.max(0, Math.min(100, progress));
  // Offset runs full-circumference (empty) → 0 (full) as progress fills.
  const targetOffset = circumference * (1 - clamped / 100);

  const sizeStyle: CSSProperties = { width: diameter, height: diameter };

  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(clamped)}
      className={cn('relative', className)}
      style={sizeStyle}
    >
      <LazyMotion features={domAnimation}>
        <svg
          aria-hidden="true"
          width={diameter}
          height={diameter}
          viewBox={`0 0 ${diameter} ${diameter}`}
          className="block"
        >
          <circle
            cx={radius}
            cy={radius}
            r={circleRadius}
            fill="none"
            strokeWidth={strokeWidth}
            strokeOpacity={trackOpacity}
            className={cn('stroke-feedback-success-accent', trackClassName)}
          />
          <m.circle
            cx={radius}
            cy={radius}
            r={circleRadius}
            fill="none"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            // Start the sweep at 12 o'clock and fill clockwise.
            transform={`rotate(-90 ${radius} ${radius})`}
            className={cn('stroke-feedback-success-accent', strokeClassName)}
            initial={false}
            animate={{ strokeDashoffset: targetOffset }}
            transition={reduce ? { duration: 0 } : MOTION_POSITION_SMALL}
          />
        </svg>
      </LazyMotion>
      {children !== undefined ? (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          {children}
        </div>
      ) : null}
    </div>
  );
}
